import { cn } from "@/lib/utils";
import * as React from "react";

interface ResizablePanelsProps {
	leftPanel: React.ReactNode;
	rightPanel: React.ReactNode;
	initialLeftWidth?: number; // As percentage (0-100)
	minLeftWidth?: number; // As percentage (0-100)
	maxLeftWidth?: number; // As percentage (0-100)
	className?: string;
	dividerClassName?: string;
}

/**
 * ResizablePanels component to create a draggable interface
 * between two side-by-side panels.
 */
export function ResizablePanels({
	leftPanel,
	rightPanel,
	initialLeftWidth = 50,
	minLeftWidth = 20,
	maxLeftWidth = 80,
	className,
	dividerClassName,
}: ResizablePanelsProps) {
	// Use state for completed drags (for React rendering)
	const [leftWidth, setLeftWidth] = React.useState(initialLeftWidth);
	const [isDragging, setIsDragging] = React.useState(false);

	// Use refs for tracking during drag (no re-renders)
	const containerRef = React.useRef<HTMLDivElement>(null);
	const leftPanelRef = React.useRef<HTMLDivElement>(null);
	const rightPanelRef = React.useRef<HTMLDivElement>(null);
	const animationFrameRef = React.useRef<number | null>(null);
	const dividerRef = React.useRef<HTMLDivElement>(null);

	const startDragging = (e: React.MouseEvent | React.TouchEvent) => {
		e.preventDefault();
		setIsDragging(true);

		const containerRect = containerRef.current?.getBoundingClientRect();
		if (!containerRef.current || !containerRect) return;

		const onMouseMove = (moveEvent: MouseEvent) => {
			// Cancel any pending animation frame
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}

			// Update panel dimensions directly for immediate response
			if (!containerRef.current) return;

			const containerRect = containerRef.current.getBoundingClientRect();
			const mouseXRelativeToContainer = moveEvent.clientX - containerRect.left;
			let newLeftWidthPercentage =
				(mouseXRelativeToContainer / containerRect.width) * 100;

			// Constrain between min and max
			newLeftWidthPercentage = Math.min(
				Math.max(newLeftWidthPercentage, minLeftWidth),
				maxLeftWidth,
			);

			// Apply changes directly to DOM for real-time response
			if (leftPanelRef.current && rightPanelRef.current) {
				leftPanelRef.current.style.width = `${newLeftWidthPercentage}%`;
				rightPanelRef.current.style.width = `${100 - newLeftWidthPercentage}%`;
			}

			// Schedule React state update at end of frame, no transitions during drag
			animationFrameRef.current = requestAnimationFrame(() => {
				setLeftWidth(newLeftWidthPercentage);
			});
		};

		const handleTouchMove = (moveEvent: TouchEvent) => {
			if (moveEvent.touches.length > 0) {
				const touch = moveEvent.touches[0];
				const mockMouseEvent = {
					clientX: touch.clientX,
					preventDefault: () => moveEvent.preventDefault(),
				} as MouseEvent;
				onMouseMove(mockMouseEvent);
			}
		};

		const handleTouchEnd = () => {
			onMouseUp();
		};

		const onMouseUp = () => {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			document.removeEventListener("touchmove", handleTouchMove);
			document.removeEventListener("touchend", handleTouchEnd);

			document.body.style.cursor = "";

			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}

			setIsDragging(false);
		};

		// Set cursor for entire document during drag
		document.body.style.cursor = "col-resize";
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		document.addEventListener("touchmove", handleTouchMove);
		document.addEventListener("touchend", handleTouchEnd);
	};

	return (
		<div
			ref={containerRef}
			className={cn("flex flex-1 w-full container-overflow-safe", className)}
		>
			{/* Left panel */}
			<div
				ref={leftPanelRef}
				className={cn(
					"h-full overflow-hidden container-overflow-safe",
					isDragging ? "" : "transition-width duration-75",
				)}
				style={{
					width: `${leftWidth}%`,
				}}
			>
				{leftPanel}
			</div>

			{/* Draggable divider */}
			<div
				ref={dividerRef}
				className={cn(
					"hover:w-4 bg-background h-full cursor-col-resize flex items-center justify-center select-none group relative flex-shrink-0",
					isDragging ? "w-4" : "w-3 transition-all duration-300 ease-in-out",
					dividerClassName,
				)}
				onMouseDown={startDragging}
				onTouchStart={startDragging}
				title="Zum Ändern der Größe ziehen"
			>
				{/* Visual indicator of draggability - visible on hover */}
				<div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-primary/10 transition-opacity duration-200" />

				{/* Grip handle - always visible */}
				<div className="w-2 h-20 rounded relative z-10 flex items-center justify-center">
					<div className="flex flex-col items-center gap-1.5">
						{[0, 1, 2].map((i) => (
							<div
								key={`grip-dot-${i}`}
								className="w-1 h-1 bg-primary rounded-full group-hover:bg-primary/90 group-active:scale-110 transition-all duration-200"
							/>
						))}
					</div>
				</div>

				{/* Tooltip indicator on hover - optional */}
				<div className="absolute top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap text-xs bg-background text-foreground shadow px-2 py-1 rounded pointer-events-none z-20">
					Zum Ändern der Größe ziehen
				</div>
			</div>

			{/* Right panel */}
			<div
				ref={rightPanelRef}
				className={cn(
					"h-full overflow-hidden container-overflow-safe",
					isDragging ? "" : "transition-width duration-75",
				)}
				style={{
					width: `${100 - leftWidth}%`,
				}}
			>
				{rightPanel}
			</div>
		</div>
	);
}
